export const en = {
  brand: "Company World Cup Predictor",
  nav: {
    dashboard: "Dashboard",
    matches: "Matches",
    leaderboard: "Leaderboard",
    profile: "Profile",
    news: "News Center",
    prizes: "Prizes",
    admin: "Admin",
    logout: "Sign out",
    // Admin sub
    users: "Users",
    tournament: "Tournament",
    predictions: "Predictions",
    rewards: "Rewards",
    content: "Content",
    reports: "Reports",
    audit: "Audit Logs",
    branding: "Branding",
  },
  login: {
    title: "Sign in",
    subtitle: "Compete with colleagues. Predict every match. Climb the leaderboard.",
    email: "Work email",
    employeeId: "Employee ID",
    employeeIdPlaceholder: "e.g. 1002",
    password: "Password",
    submit: "Sign in securely",
    forgot: "Forgot password?",
    forgotHint: "Forgot your password? Please contact your administrator.",
    reset: "Reset password",
    resetTitle: "Reset your password",
    newPassword: "New password",
    confirmReset: "Update password",
    hero: "FIFA World Cup",
    invalid: "Invalid employee ID or password",
    rateLimited: "Too many attempts. Please wait a few minutes.",
    success: "Welcome back!",
    resetSent: "If the account exists, the password has been reset.",
  },
  changePassword: {
    title: "Change password",
    subtitle: "Update your account password.",
    forcedSubtitle: "For your security, you must set a new password before continuing.",
    current: "Current password",
    new: "New password",
    confirm: "Confirm new password",
    submit: "Update password",
    success: "Password changed successfully",
    error: "Unable to change password",
    weak: "Password does not meet the requirements",
    mismatch: "Passwords do not match",
    hint: "Use at least 8 characters with uppercase, lowercase, a number and a special character.",
    rules: {
      len: "At least 8 characters",
      upper: "One uppercase letter",
      lower: "One lowercase letter",
      number: "One number",
      special: "One special character",
    },
  },
  dashboard: {
    welcome: "Welcome back",
    yourRank: "Your rank",
    totalPoints: "Total points",
    accuracy: "Accuracy",
    predictions: "Predictions",
    departmentRank: "Department rank",
    upcoming: "Upcoming matches",
    recent: "Your recent predictions",
    latestNews: "Latest news",
    announcements: "Company announcements",
    noPredictions: "No predictions yet. Start picking!",
    noNews: "No news yet.",
    noAnnouncements: "No announcements.",
    of: "of",
    predictNow: "Predict",
    viewAll: "View all",
  },
  matches: {
    title: "Match Predictions",
    subtitle: "Predict scores before kickoff. Locked at first whistle.",
    locked: "Locked",
    live: "LIVE",
    finished: "Finished",
    scheduled: "Scheduled",
    yourPick: "Your pick",
    actualScore: "Final score",
    submit: "Submit prediction",
    update: "Update",
    points: "pts",
    pointsAwarded: "+{n} pts",
    group: "Group",
    filterAll: "All",
    venue: "Venue",
    kickoff: "Kickoff",
    saved: "Prediction saved",
    closed: "Predictions are closed by admin",
    pickWillLock: "Locks at kickoff",
  },
  leaderboard: {
    title: "Leaderboard",
    subtitle: "Live ranking of all predictors across the company.",
    overall: "Overall",
    department: "By department",
    rank: "Rank",
    name: "Name",
    deptCol: "Department",
    points: "Points",
    you: "You",
    top10: "Top 10",
    filterDept: "Filter by department",
    all: "All departments",
  },
  profile: {
    title: "My Profile",
    info: "Information",
    awards: "Achievements & awards",
    stats: "Statistics",
    accuracy: "Prediction accuracy",
    points: "Total points",
    rank: "Current rank",
    deptRank: "Department rank",
    settled: "Settled predictions",
    changePassword: "Change password",
    newPassword: "New password",
    update: "Update password",
    passwordUpdated: "Password updated",
  },
  news: {
    title: "News Center",
    subtitle: "World Cup news, schedules, results and tournament stats.",
    tabs: { all: "All", world_cup: "World Cup", team: "Team", stats: "Stats", schedule: "Schedule" },
    standings: "Group standings",
    schedule: "Match schedule",
    results: "Match results",
  },
  prizes: {
    title: "Prizes & Rewards",
    subtitle: "Compete for these prizes — winners are auto-assigned by final ranking.",
    winners: "Winners",
    ranks: "Ranks",
  },
  admin: {
    dashboardTitle: "Admin Console",
    overview: "Overview",
    totalUsers: "Total employees",
    activeUsers: "Active",
    totalMatches: "Total matches",
    finishedMatches: "Finished",
    totalPredictions: "Predictions",
    participants: "Participants",
    participationRate: "Participation rate",
    users: {
      title: "User Management",
      add: "Add user",
      import: "Import CSV",
      search: "Search by name or ID",
      empty: "No users",
      created: "User created",
      updated: "User updated",
      deactivated: "User deactivated",
      name: "Name",
      department: "Department",
      role: "Role",
      active: "Active",
      actions: "Actions",
      edit: "Edit",
      deactivate: "Deactivate",
      activate: "Activate",
      saveBtn: "Save",
      password: "Password",
      passwordHint: "leave empty for default 123456",
      resetPassword: "Reset password",
      resetConfirm: "Reset this user's password to the default (123456)? They will be asked to change it on next login.",
      resetDone: "Password reset to default (123456)",
    },
    tournament: {
      title: "Tournament Management",
      teams: "Teams",
      matches: "Matches",
      addTeam: "Add team",
      addMatch: "Add match",
      result: "Set result",
      saveResult: "Save result",
      teamName: "Team name",
      teamCode: "Code",
      flag: "Flag",
      group: "Group",
      home: "Home",
      away: "Away",
      kickoff: "Kickoff",
      stage: "Stage",
      venue: "Venue",
    },
    predictions: {
      title: "Prediction Management",
      window: "Prediction window",
      open: "Open",
      closed: "Closed",
      scoring: "Scoring rules",
      exact: "Exact score",
      outcomeDiff: "Correct outcome + goal difference",
      outcome: "Correct outcome only",
      wrong: "Wrong",
      save: "Save changes",
      saved: "Settings saved",
    },
    rewards: {
      title: "Rewards Management",
      add: "Add prize",
      assign: "Assign winners",
      title_field: "Title",
      descr: "Description",
      rankFrom: "Rank from",
      rankTo: "Rank to",
      icon: "Icon",
      assigned: "Winners assigned",
    },
    content: {
      title: "Content Management",
      news: "News",
      announcements: "Announcements",
      addNews: "Publish news",
      addAnnouncement: "Post announcement",
      ttl: "Title (EN)",
      ttlAr: "Title (AR)",
      body: "Body (EN)",
      bodyAr: "Body (AR)",
      image: "Image URL",
      category: "Category",
      pinned: "Pinned",
    },
    reports: {
      title: "Reports & Analytics",
      departments: "Department performance",
      accuracy: "Prediction accuracy",
      avgPoints: "Avg points",
      members: "Members",
      total: "Total",
      exactScores: "Exact scores",
      correctOutcome: "Correct outcomes",
      exportExcel: "Export CSV",
    },
    audit: {
      title: "Audit Logs",
      action: "Action",
      user: "User",
      resource: "Resource",
      ip: "IP",
      time: "Time",
      filterAction: "Filter action",
    },
    branding: {
      title: "Branding & Identity",
      subtitle: "Customize your platform's look — logo, colors, login image and favicon. Changes apply instantly across the app.",
      identity: "Identity",
      nameEn: "Platform name (EN)",
      nameAr: "Platform name (AR)",
      colors: "Colors",
      images: "Images",
      logo: "Company logo (top bar)",
      favicon: "Site icon (favicon)",
      symbol: "Brand symbol (watermark)",
      loginImage: "Login page image",
      background: "Page background",
      bg_pattern: "Brand watermark",
      bg_gradient: "Gradient",
      bg_solid: "Plain",
      bg_image: "Custom image",
      bgImage: "Background image",
      bgPatternHint: "Uses your brand symbol as a subtle watermark across all pages.",
      loginTaglineEn: "Login tagline (EN)",
      loginTaglineAr: "Login tagline (AR)",
      upload: "Upload",
      uploading: "Uploading...",
      pasteUrl: "or paste image URL",
      preview: "Live preview",
      save: "Save changes",
      saved: "Branding updated",
      reset: "Reset to defaults",
      resetDone: "Reset to defaults",
      sampleBtn: "Primary button",
      sampleBadge: "Accent",
      colorPrimary: "Primary",
      colorPrimaryFg: "Primary text",
      colorAccent: "Accent",
      colorAccentFg: "Accent text",
      colorBackground: "Background",
      colorForeground: "Text",
      colorCard: "Card / Surface",
      colorSecondary: "Secondary",
      colorBorder: "Border",
      uploadError: "Upload failed",
    },
  },
  common: {
    save: "Save",
    cancel: "Cancel",
    confirm: "Confirm",
    delete: "Delete",
    create: "Create",
    loading: "Loading...",
    refresh: "Refresh",
    yes: "Yes",
    no: "No",
    ok: "OK",
    optional: "optional",
    notify: "Notification",
    notifications: "Notifications",
    markAllRead: "Mark all as read",
    noNotifications: "No notifications",
    switchLang: "العربية",
    switchTheme: "Theme",
    logout: "Sign out",
  },
};

export const ar = {
  brand: "توقعات كأس العالم للشركة",
  nav: {
    dashboard: "اللوحة الرئيسية",
    matches: "المباريات",
    leaderboard: "لوحة الترتيب",
    profile: "الملف الشخصي",
    news: "مركز الأخبار",
    prizes: "الجوائز",
    admin: "الإدارة",
    logout: "تسجيل الخروج",
    users: "المستخدمون",
    tournament: "البطولة",
    predictions: "التوقعات",
    rewards: "الجوائز",
    content: "المحتوى",
    reports: "التقارير",
    audit: "سجلات التدقيق",
    branding: "الهوية البصرية",
  },
  login: {
    title: "تسجيل الدخول",
    subtitle: "نافس زملاءك. توقّع كل مباراة. اصعد لأعلى لوحة الترتيب.",
    email: "البريد الإلكتروني للعمل",
    employeeId: "الرقم الوظيفي",
    employeeIdPlaceholder: "مثال: 1002",
    password: "كلمة المرور",
    submit: "تسجيل دخول آمن",
    forgot: "هل نسيت كلمة المرور؟",
    forgotHint: "هل نسيت كلمة المرور؟ يُرجى التواصل مع مسؤول النظام.",
    reset: "إعادة تعيين كلمة المرور",
    resetTitle: "إعادة تعيين كلمة المرور",
    newPassword: "كلمة المرور الجديدة",
    confirmReset: "تحديث كلمة المرور",
    hero: "كأس العالم FIFA",
    invalid: "الرقم الوظيفي أو كلمة المرور غير صحيحة",
    rateLimited: "محاولات كثيرة. حاول مرة أخرى بعد دقائق قليلة.",
    success: "مرحباً بعودتك!",
    resetSent: "إذا كان الحساب موجوداً، فقد تم تحديث كلمة المرور.",
  },
  changePassword: {
    title: "تغيير كلمة المرور",
    subtitle: "حدّث كلمة مرور حسابك.",
    forcedSubtitle: "لحماية حسابك، يجب تعيين كلمة مرور جديدة قبل المتابعة.",
    current: "كلمة المرور الحالية",
    new: "كلمة المرور الجديدة",
    confirm: "تأكيد كلمة المرور الجديدة",
    submit: "تحديث كلمة المرور",
    success: "تم تغيير كلمة المرور بنجاح",
    error: "تعذّر تغيير كلمة المرور",
    weak: "كلمة المرور لا تستوفي المتطلبات",
    mismatch: "كلمتا المرور غير متطابقتين",
    hint: "استخدم 8 أحرف على الأقل تتضمن حرفاً كبيراً وحرفاً صغيراً ورقماً ورمزاً خاصاً.",
    rules: {
      len: "8 أحرف على الأقل",
      upper: "حرف كبير واحد",
      lower: "حرف صغير واحد",
      number: "رقم واحد",
      special: "رمز خاص واحد",
    },
  },
  dashboard: {
    welcome: "مرحباً بعودتك",
    yourRank: "ترتيبك",
    totalPoints: "إجمالي النقاط",
    accuracy: "نسبة الدقة",
    predictions: "التوقعات",
    departmentRank: "ترتيب القسم",
    upcoming: "المباريات القادمة",
    recent: "توقعاتك الأخيرة",
    latestNews: "آخر الأخبار",
    announcements: "إعلانات الشركة",
    noPredictions: "لا توجد توقعات بعد. ابدأ بالتوقع!",
    noNews: "لا توجد أخبار بعد.",
    noAnnouncements: "لا توجد إعلانات.",
    of: "من",
    predictNow: "توقّع",
    viewAll: "عرض الكل",
  },
  matches: {
    title: "توقعات المباريات",
    subtitle: "توقّع النتائج قبل بداية المباراة. تُقفل عند صافرة البداية.",
    locked: "مغلق",
    live: "مباشر",
    finished: "انتهت",
    scheduled: "مجدولة",
    yourPick: "توقعك",
    actualScore: "النتيجة النهائية",
    submit: "حفظ التوقع",
    update: "تحديث",
    points: "نقطة",
    pointsAwarded: "+{n} نقطة",
    group: "المجموعة",
    filterAll: "الكل",
    venue: "الملعب",
    kickoff: "بداية المباراة",
    saved: "تم حفظ التوقع",
    closed: "التوقعات مغلقة من قبل المسؤول",
    pickWillLock: "تُقفل عند بداية المباراة",
  },
  leaderboard: {
    title: "لوحة الترتيب",
    subtitle: "الترتيب الفوري لجميع المتنبئين في الشركة.",
    overall: "العام",
    department: "حسب القسم",
    rank: "المركز",
    name: "الاسم",
    deptCol: "القسم",
    points: "النقاط",
    you: "أنت",
    top10: "أفضل 10",
    filterDept: "تصفية بالقسم",
    all: "جميع الأقسام",
  },
  profile: {
    title: "ملفي الشخصي",
    info: "المعلومات",
    awards: "الإنجازات والجوائز",
    stats: "الإحصائيات",
    accuracy: "دقة التوقعات",
    points: "إجمالي النقاط",
    rank: "الترتيب الحالي",
    deptRank: "ترتيب القسم",
    settled: "التوقعات المحتسبة",
    changePassword: "تغيير كلمة المرور",
    newPassword: "كلمة المرور الجديدة",
    update: "تحديث كلمة المرور",
    passwordUpdated: "تم تحديث كلمة المرور",
  },
  news: {
    title: "مركز الأخبار",
    subtitle: "أخبار كأس العالم، الجداول، النتائج، وإحصائيات البطولة.",
    tabs: { all: "الكل", world_cup: "كأس العالم", team: "الفرق", stats: "إحصائيات", schedule: "الجدول" },
    standings: "ترتيب المجموعات",
    schedule: "جدول المباريات",
    results: "نتائج المباريات",
  },
  prizes: {
    title: "الجوائز",
    subtitle: "نافس على هذه الجوائز — يُعيَّن الفائزون تلقائياً وفق الترتيب النهائي.",
    winners: "الفائزون",
    ranks: "المراكز",
  },
  admin: {
    dashboardTitle: "لوحة الإدارة",
    overview: "نظرة عامة",
    totalUsers: "إجمالي الموظفين",
    activeUsers: "نشطون",
    totalMatches: "إجمالي المباريات",
    finishedMatches: "المنتهية",
    totalPredictions: "التوقعات",
    participants: "المشاركون",
    participationRate: "معدل المشاركة",
    users: {
      title: "إدارة المستخدمين",
      add: "إضافة مستخدم",
      import: "استيراد CSV",
      search: "بحث بالاسم أو الرقم الوظيفي",
      empty: "لا يوجد مستخدمون",
      created: "تم إنشاء المستخدم",
      updated: "تم التحديث",
      deactivated: "تم التعطيل",
      name: "الاسم",
      department: "القسم",
      role: "الدور",
      active: "نشط",
      actions: "إجراءات",
      edit: "تعديل",
      deactivate: "تعطيل",
      activate: "تفعيل",
      saveBtn: "حفظ",
      password: "كلمة المرور",
      passwordHint: "اتركها فارغة للافتراضي 123456",
      resetPassword: "إعادة تعيين كلمة المرور",
      resetConfirm: "إعادة تعيين كلمة مرور هذا المستخدم إلى الافتراضية (123456)؟ سيُطلب منه تغييرها عند الدخول التالي.",
      resetDone: "تمت إعادة كلمة المرور إلى الافتراضية (123456)",
    },
    tournament: {
      title: "إدارة البطولة",
      teams: "الفرق",
      matches: "المباريات",
      addTeam: "إضافة فريق",
      addMatch: "إضافة مباراة",
      result: "تسجيل النتيجة",
      saveResult: "حفظ النتيجة",
      teamName: "اسم الفريق",
      teamCode: "الرمز",
      flag: "العلم",
      group: "المجموعة",
      home: "صاحب الأرض",
      away: "الضيف",
      kickoff: "موعد البداية",
      stage: "المرحلة",
      venue: "الملعب",
    },
    predictions: {
      title: "إدارة التوقعات",
      window: "نافذة التوقعات",
      open: "مفتوحة",
      closed: "مغلقة",
      scoring: "قواعد الاحتساب",
      exact: "نتيجة دقيقة",
      outcomeDiff: "نتيجة + فارق الأهداف",
      outcome: "النتيجة فقط",
      wrong: "خاطئ",
      save: "حفظ التغييرات",
      saved: "تم الحفظ",
    },
    rewards: {
      title: "إدارة الجوائز",
      add: "إضافة جائزة",
      assign: "تعيين الفائزين",
      title_field: "العنوان",
      descr: "الوصف",
      rankFrom: "من المركز",
      rankTo: "إلى المركز",
      icon: "الرمز",
      assigned: "تم تعيين الفائزين",
    },
    content: {
      title: "إدارة المحتوى",
      news: "الأخبار",
      announcements: "الإعلانات",
      addNews: "نشر خبر",
      addAnnouncement: "نشر إعلان",
      ttl: "العنوان (إنجليزي)",
      ttlAr: "العنوان (عربي)",
      body: "النص (إنجليزي)",
      bodyAr: "النص (عربي)",
      image: "رابط الصورة",
      category: "التصنيف",
      pinned: "مثبت",
    },
    reports: {
      title: "التقارير والتحليلات",
      departments: "أداء الأقسام",
      accuracy: "دقة التوقعات",
      avgPoints: "متوسط النقاط",
      members: "الأعضاء",
      total: "الإجمالي",
      exactScores: "النتائج الدقيقة",
      correctOutcome: "نتائج صحيحة",
      exportExcel: "تصدير CSV",
    },
    audit: {
      title: "سجلات التدقيق",
      action: "الإجراء",
      user: "المستخدم",
      resource: "المورد",
      ip: "IP",
      time: "الوقت",
      filterAction: "تصفية بالإجراء",
    },
    branding: {
      title: "الهوية البصرية والعلامة التجارية",
      subtitle: "خصّص مظهر المنصة — الشعار والألوان وصورة تسجيل الدخول والأيقونة. تُطبّق التغييرات فوراً على كامل الموقع.",
      identity: "الهوية",
      nameEn: "اسم المنصة (إنجليزي)",
      nameAr: "اسم المنصة (عربي)",
      colors: "الألوان",
      images: "الصور",
      logo: "شعار الشركة (الشريط العلوي)",
      favicon: "أيقونة الموقع (favicon)",
      symbol: "رمز العلامة (علامة مائية)",
      loginImage: "صورة صفحة تسجيل الدخول",
      background: "خلفية الصفحات",
      bg_pattern: "علامة مائية للعلامة",
      bg_gradient: "تدرّج لوني",
      bg_solid: "سادة",
      bg_image: "صورة مخصّصة",
      bgImage: "صورة الخلفية",
      bgPatternHint: "تستخدم رمز علامتك التجارية كعلامة مائية خفيفة في كل الصفحات.",
      loginTaglineEn: "شعار نصي لتسجيل الدخول (إنجليزي)",
      loginTaglineAr: "شعار نصي لتسجيل الدخول (عربي)",
      upload: "رفع",
      uploading: "جارٍ الرفع...",
      pasteUrl: "أو الصق رابط الصورة",
      preview: "معاينة حيّة",
      save: "حفظ التغييرات",
      saved: "تم تحديث الهوية البصرية",
      reset: "إعادة للوضع الافتراضي",
      resetDone: "تمت الإعادة للوضع الافتراضي",
      sampleBtn: "زر رئيسي",
      sampleBadge: "مميّز",
      colorPrimary: "الأساسي",
      colorPrimaryFg: "نص الأساسي",
      colorAccent: "المميّز",
      colorAccentFg: "نص المميّز",
      colorBackground: "الخلفية",
      colorForeground: "النص",
      colorCard: "البطاقات / الأسطح",
      colorSecondary: "الثانوي",
      colorBorder: "الحدود",
      uploadError: "فشل الرفع",
    },
  },
  common: {
    save: "حفظ",
    cancel: "إلغاء",
    confirm: "تأكيد",
    delete: "حذف",
    create: "إنشاء",
    loading: "جارٍ التحميل...",
    refresh: "تحديث",
    yes: "نعم",
    no: "لا",
    ok: "حسناً",
    optional: "اختياري",
    notify: "إشعار",
    notifications: "الإشعارات",
    markAllRead: "تعليم الكل كمقروء",
    noNotifications: "لا توجد إشعارات",
    switchLang: "English",
    switchTheme: "المظهر",
    logout: "تسجيل الخروج",
  },
};
